import { create } from "zustand";

export type ReferenceSidebarHoverPreviewOptions = {
  /** When false, highlights still update but the document does not scroll to the match (e.g. multi-link popup). */
  scrollToMatch?: boolean;
};

type ReferenceSidebarHoverPreviewState = {
  /** True while pointer is over a references sidebar row (debounced leave). */
  sidebarHoverActive: boolean;
  /** Open document id the sidebar references tab is for; must match DocumentRenderer currentDocId. */
  openDocumentId: string | null;
  previewMatchText: string | undefined;
  previewChunkText: string | undefined;
  /** Block element ids on the open document when match text is missing (e.g. workspace links). */
  previewHighlightIds: string[] | undefined;
  /** Whether preview should scroll the open document to the highlighted text (sidebar defaults to true). */
  previewScrollToMatch: boolean;
  /**
   * Document link id(s) under the pointer in the open document body (inverse of sidebar hover).
   * Used to highlight matching rows in the references sidebar.
   */
  contentHoverLinkIds: string[];
  /**
   * When set, all injected segments with this `data-doc-link-group` (split merged reference)
   * show consolidated hover underline in the document body.
   */
  contentHoverDocLinkGroupId: string | null;
  beginHover: (
    openDocumentId: string,
    previewMatchText?: string,
    previewChunkText?: string,
    previewHighlightIds?: string[],
    options?: ReferenceSidebarHoverPreviewOptions
  ) => void;
  endHover: () => void;
  beginContentHover: (
    linkIds: string[],
    docLinkGroupId?: string | null
  ) => void;
  endContentHover: () => void;
};

export const useReferenceSidebarHoverPreviewStore =
  create<ReferenceSidebarHoverPreviewState>((set) => ({
    sidebarHoverActive: false,
    openDocumentId: null,
    previewMatchText: undefined,
    previewChunkText: undefined,
    previewHighlightIds: undefined,
    previewScrollToMatch: true,
    contentHoverLinkIds: [],
    contentHoverDocLinkGroupId: null,
    beginHover: (
      openDocumentId,
      previewMatchText,
      previewChunkText,
      previewHighlightIds,
      options
    ) =>
      set({
        sidebarHoverActive: true,
        openDocumentId,
        previewMatchText,
        previewChunkText,
        previewHighlightIds,
        previewScrollToMatch: options?.scrollToMatch !== false,
      }),
    endHover: () =>
      set({
        sidebarHoverActive: false,
        openDocumentId: null,
        previewMatchText: undefined,
        previewChunkText: undefined,
        previewHighlightIds: undefined,
        previewScrollToMatch: true,
      }),
    beginContentHover: (linkIds, docLinkGroupId) =>
      set({
        contentHoverLinkIds:
          linkIds.length > 0 ? Array.from(new Set(linkIds)) : [],
        contentHoverDocLinkGroupId:
          docLinkGroupId && docLinkGroupId.trim().length > 0
            ? docLinkGroupId
            : null,
      }),
    endContentHover: () =>
      set({ contentHoverLinkIds: [], contentHoverDocLinkGroupId: null }),
  }));
